import React from "react";
import PageHero, { PageCTA } from "../components/PageHero";
import { Check, KeyRound, Lock, Cog } from "lucide-react";
import { heroImages } from "../data/mock";

const RemplacementSerrure = () => {
  const serrures = [
    { name: "Serrure 3 points", certification: "A2P *", desc: "Sécurité renforcée pour porte d'appartement standard.", image: heroImages.lock1 },
    { name: "Serrure 5 points", certification: "A2P **", desc: "Protection accrue avec verrouillage haut, milieu et bas.", image: heroImages.lock2 },
    { name: "Serrure 7 points", certification: "A2P ***", desc: "Niveau de sécurité maximum pour porte d'entrée principale.", image: heroImages.lock3 },
  ];

  return (
    <div>
      <PageHero
        title="Remplacement de serrure Fichet"
        subtitle="Installation et changement de serrures haute sécurité A2P par nos serruriers certifiés Fichet."
        breadcrumb="Remplacement de serrure"
        badge="Serrurerie haute sécurité"
        image={heroImages.remplacementSerrure}
      />

      {/* INTRO */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Le cœur de votre sécurité</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-tight mb-6">
              Des serrures Fichet certifiées A2P
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              La serrure est le premier rempart contre l'effraction. Fichet Serrurerie remplace votre mécanisme par une serrure haute sécurité 3, 5 ou 7 points certifiée A2P.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Nos techniciens spécialistes Fichet adaptent la solution à votre porte existante, sans travaux lourds, et vous garantissent une pose parfaite en moins de 2 heures.
            </p>
          </div>
          <img src={heroImages.remplacementSerrure} alt="Serrure haute sécurité" className="w-full h-[460px] object-cover" />
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: KeyRound, title: "Clés protégées", desc: "Nos cylindres Fichet 787 Z et Alto+ sont protégés contre la copie et le crochetage." },
            { icon: Lock, title: "Anti-effraction", desc: "Protection contre le percçage, le crochetage, la casse et le bumping." },
            { icon: Cog, title: "Compatible toute porte", desc: "Adaptation à votre porte existante, bois, métal ou blindée." },
          ].map((b, i) => (
            <div key={i} className="bg-white p-8 border-t-4 border-[#C9A227] hover:shadow-xl transition-shadow">
              <b.icon className="w-10 h-10 text-black mb-4" />
              <h3 className="text-xl font-bold text-black mb-3 tracking-tight">{b.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MODELS */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Notre gamme</div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-black">Serrures 3, 5 et 7 points</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {serrures.map((s, i) => (
              <div key={i} className="bg-white border border-gray-200 hover:border-[#C9A227] transition-colors group">
                <div className="h-72 overflow-hidden bg-gray-100">
                  <img src={s.image} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="inline-block bg-[#123B2E] text-[#C9A227] text-xs font-bold tracking-widest uppercase px-2.5 py-1 mb-3">
                    {s.certification}
                  </div>
                  <h3 className="text-2xl font-black text-black tracking-tight mb-3">{s.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN TO REPLACE */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Quand nous appeler ?</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black">Les signes qu'il faut remplacer</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              "Aménagement dans un nouveau logement",
              "Perte ou vol de vos clés",
              "Serrure ancienne ou usée",
              "Après une tentative d'effraction",
              "Serrure grincée ou difficile à manipuler",
              "Passage à une serrure haute sécurité",
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white p-5">
                <div className="w-6 h-6 bg-[#C9A227] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-black" />
                </div>
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageCTA title="Remplacez votre serrure dès aujourd'hui" subtitle="Devis gratuit et pose en moins de 2 heures" />
    </div>
  );
};

export default RemplacementSerrure;
